import {
  GETTER_LOADED,
  MUTATE_LOADED,
  GETTER_TOKENS,
  GETTER_DOCTOR_PATIENT_PARTNER,
  GETTER_NOW_APPOINTMENTS,
  GETTER_FUTURE_APPOINTMENTS,
  GETTER_DENIED_APPOINTMENTS,
  GETTER_PENDING_APPOINTMENTS,
  GETTER_NEW_REQUEST_APPOINTMENTS,
  GETTER_ALL_APPOINTMENTS,
  GETTER_PRIORITY_APPOINTMENTS,
  ACTION_REFRESH_APPOINTMENTS,
  MUTATE_SET_APPOINTMENTS,
  MUTATE_CLEAR_APPOINTMENTS_MODULE,
  ACTION_CLEAR_APPOINTMENTS_MODULE,
} from './types';
import {APPOINTMENT_TYPES, AppointmentStatusToEventTypeMapper} from '@/constants';
import {AppointmentsService} from '@/services/appointments';
import {sortByNearestDate} from '@/utils/dates';
import {getFilterType, isDeniedWithinVisibilityWindow} from '@/utils/appointments';
import {logger} from '@/utils/logger';

const {
  NOW,
  FUTURE,
  DENIED,
  PENDING,
  NEW_REQUEST,
} = APPOINTMENT_TYPES;

const getDefaultAppointmentsModule = () => {
  return {
    loaded: false,
    appointments: {
      [NOW]: [],
      [FUTURE]: [],
      [DENIED]: [],
      [PENDING]: [],
      [NEW_REQUEST]: [],
    },
  }
}
const state = getDefaultAppointmentsModule();

const getters = {
  [GETTER_LOADED]: ({loaded}) => loaded,

  [GETTER_NOW_APPOINTMENTS]: ({appointments}, getters) => appointments[NOW]
    .map((appointment) => ({
      type: NOW,
      _filterType: getFilterType(appointment),
      ...getters[GETTER_DOCTOR_PATIENT_PARTNER][appointment.accesstoken],
      ...appointment
    }))
    .sort(sortByNearestDate),

  [GETTER_FUTURE_APPOINTMENTS]: ({appointments}, getters) => appointments[FUTURE]
    .map((appointment) => ({
      type: FUTURE,
      _filterType: getFilterType(appointment),
      ...getters[GETTER_DOCTOR_PATIENT_PARTNER][appointment.accesstoken],
      ...appointment,
    }))
    .sort(sortByNearestDate),

  [GETTER_DENIED_APPOINTMENTS]: ({appointments}, getters) => appointments[DENIED]
    .map((appointment) => ({
      type: DENIED,
      _filterType: getFilterType(appointment),
      ...getters[GETTER_DOCTOR_PATIENT_PARTNER][appointment.accesstoken],
      ...appointment,
    }))
    .sort(sortByNearestDate),

  [GETTER_PENDING_APPOINTMENTS]: ({appointments}, getters) => appointments[PENDING]
    .map((appointment) => ({
      type: PENDING,
      _filterType: getFilterType(appointment),
      ...getters[GETTER_DOCTOR_PATIENT_PARTNER][appointment.accesstoken],
      ...appointment,
    }))
    .sort(sortByNearestDate),

  [GETTER_NEW_REQUEST_APPOINTMENTS]: ({appointments}) => appointments[NEW_REQUEST]
    .map((appointment) => ({
      type: NEW_REQUEST,
      _filterType: getFilterType(appointment),
      ...appointment
    }))
    .sort(sortByNearestDate),

  [GETTER_ALL_APPOINTMENTS]: (state, getters) => {
    const appointmentsGetters = [
      GETTER_NOW_APPOINTMENTS,
      GETTER_FUTURE_APPOINTMENTS,
      GETTER_DENIED_APPOINTMENTS,
      GETTER_PENDING_APPOINTMENTS,
    ];
    const allAppointments = appointmentsGetters
      .map((getterKey) => {
        return getters[getterKey];
      })
      .flat();
    return allAppointments;
  },
  [GETTER_PRIORITY_APPOINTMENTS]: (state, getters) => {
    if (getters[GETTER_NOW_APPOINTMENTS].length) {
      return getters[GETTER_NOW_APPOINTMENTS];
    } else if (getters[GETTER_FUTURE_APPOINTMENTS].length) {
      return getters[GETTER_FUTURE_APPOINTMENTS];
    } else if (getters[GETTER_PENDING_APPOINTMENTS].length) {
      return getters[GETTER_PENDING_APPOINTMENTS];
    } else if (getters[GETTER_DENIED_APPOINTMENTS].length) {
      const recentlyDenied = getters[GETTER_DENIED_APPOINTMENTS]
        .filter(isDeniedWithinVisibilityWindow);

      if (recentlyDenied.length) {
        return recentlyDenied;
      }
    }

    return null;
  },
};

const actions = {
  [ACTION_REFRESH_APPOINTMENTS]: async ({commit, rootGetters}) => {
    const appointmentsRequests = rootGetters[GETTER_TOKENS]
      .map(({accesstoken}) => {
        return AppointmentsService.getAllActiveAppointments(accesstoken);
      });
    const newAppointments = {
      [NOW]: [],
      [FUTURE]: [],
      [DENIED]: [],
      [PENDING]: [],
      [NEW_REQUEST]: [],
    };

    let appointmentsRequestsResponses = [];

    try {
      appointmentsRequestsResponses = await Promise.all(appointmentsRequests);
    } catch (error) {
      // Preserve last-known-good appointments on failure.
      logger.error('AppointmentsService.getAllActiveAppointments XHR failed', {}, error);
      throw error;
    }

    const {data: newRequestsData} = await AppointmentsService.getAppointmentRequests();

    appointmentsRequestsResponses.forEach((responses, index) => {
      const {accesstoken} = rootGetters[GETTER_TOKENS][index];
      const [nowFutureResponse, pendingResponse, deniedResponse] = responses;

      if (nowFutureResponse.data.now) {
        newAppointments[NOW].push({...nowFutureResponse.data.now, accesstoken});
      }

      if (nowFutureResponse.data.future) {
        nowFutureResponse.data.future.forEach((appointment) => {
          newAppointments[FUTURE].push({...appointment, accesstoken});
        });
      }

      if (pendingResponse.data) {
        pendingResponse.data.forEach((appointment) => {
          newAppointments[PENDING].push({...appointment, accesstoken});
        });
      }

      if (deniedResponse.data) {
        deniedResponse.data.forEach((appointment) => {
          newAppointments[DENIED].push({...appointment, accesstoken});
        });
      }
    });

    if (newRequestsData) {
      newAppointments[NEW_REQUEST] = newRequestsData;

      newRequestsData.forEach((appointment) => {
        const matchingEventType = AppointmentStatusToEventTypeMapper[appointment.status];

        if (matchingEventType) {
          newAppointments[matchingEventType].push(appointment);
        }
      });
    }

    commit(MUTATE_LOADED, true);
    commit(MUTATE_SET_APPOINTMENTS, newAppointments);
  },
  [ACTION_CLEAR_APPOINTMENTS_MODULE]: ({commit}) => {
    commit(MUTATE_CLEAR_APPOINTMENTS_MODULE);
  }
};

const mutations = {
  [MUTATE_LOADED]: (state, isLoaded) => {
    state.loaded = isLoaded;
  },
  [MUTATE_SET_APPOINTMENTS]: (state, newAppointments) => {
    state.appointments = newAppointments;
  },
  [MUTATE_CLEAR_APPOINTMENTS_MODULE]: (state) => {
    Object.assign(state, getDefaultAppointmentsModule());
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
